import { Box, Stack } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { useConfirm } from '~/components/ConfirmDialog'
import { DEMO_MODE_COLOR } from '~/features/demoMode/constants'
import useSetDemoMode from '~/features/demoMode/useSetDemoMode'
import { useIsDemoMode } from '~/store/slices/demoMode'

import { BannerBar, BannerButton, BannerTypography } from './components'

/**
 * Loud purple banner shown across the top of every page while demo mode is active, with
 * the only exit affordance. Exiting requires an explicit confirmation because the app
 * reloads straight into real customer data — a misclick mid-demo would be very bad.
 */
export default function DemoModeBanner() {
  const t = useTranslations('layouts.banners')
  const isDemoMode = useIsDemoMode()
  const setDemoMode = useSetDemoMode()
  const { confirm, confirmationDialog } = useConfirm()
  const [exiting, setExiting] = useState(false)

  if (!isDemoMode) return null

  const handleExit = async () => {
    const confirmed = await confirm({
      title: t('demoMode_exitConfirmTitle'),
      body: t('demoMode_exitConfirmBody'),
      confirmLabel: t('demoMode_exitConfirmAction'),
      cancelLabel: t('demoMode_exitConfirmCancel'),
      destructive: true,
      maxWidth: 'sm'
    })
    if (!confirmed) return

    setExiting(true)
    await setDemoMode(false)
  }

  return (
    <BannerBar backgroundColor={DEMO_MODE_COLOR}>
      <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center" spacing={6}>
        <Box role="status">
          <BannerTypography color="white">
            {t.rich('demoMode_active', { b: (chunks) => <b>{chunks}</b> })}
          </BannerTypography>
        </Box>

        <BannerButton darkBackground loading={exiting} onClick={handleExit}>
          {t('demoMode_exitButton')}
        </BannerButton>
      </Stack>
      {confirmationDialog}
    </BannerBar>
  )
}
